import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import i18n from 'strings/i18n'
import config from 'const/config'
import { clientUrls, lwsBacktoMerchant } from 'const/account/clients'
import { handleMakeAPaymentOffline } from 'helpers/handleMakeAPaymentOffline'
import { Button as BrandRefreshButton } from '@syf-cl/button'
import { MakeAPaymentOfflineButtonForLWSLegacy } from 'pages/AccountsDashboard/TechnicalError/subcomponents'
import triggerAnalyticsEvent from 'helpers/triggerAnalyticsEvent'
import isProductionEnv from 'helpers/isProductionEnv'
import redirectToUrl from 'helpers/redirectToUrl'
import { isBrandRefreshEnabled } from 'helpers/getBrandRefreshPhase'
import {
  ButtonContainer,
  ErrorContent,
  ErrorImage,
  ErrorMessage,
  ErrorTitle,
  GoBackLegacy,
  LWSErrorContainer,
  SignInLegacy
} from './subcomponents'

const LWS_ERROR_DATA_ATTRIBUTES = {
  'data-analytics': 'clicktrackingevent',
  'data-object': 'button',
  'data-type': 'lws'
} as const

const LWSError = () => {
  const client = localStorage.getItem('client')
  const lwsRedirectUri = sessionStorage.getItem('lwsRedirectUri')
  const { state } = useLocation()

  const handleSignIn = () => {
    sessionStorage.removeItem('lwsRedirectUri')

    if (isProductionEnv()) {
      const clientUrl = clientUrls[client]
      if (clientUrl) {
        const clientRedirectUrl = `${clientUrl}/login`
        redirectToUrl(clientRedirectUrl)
        return
      }
    }
    const encodedClient = encodeURIComponent(client)
    window.location.href = `${window.location.origin}/accounts/login/${encodedClient}`
  }

  const handleBackToMerchant = () => {
    sessionStorage.removeItem('lwsRedirectUri')
    window.location.href = `${lwsRedirectUri}${state?.searchParams}`
  }

  useEffect(() => {
    triggerAnalyticsEvent({
      errorMessage: 'we can’t load your account at this time',
      pageFunction: 'lws',
      pageKind: 'dashboard',
      pageName: state?.searchParams
        ? 'rate limiting error'
        : 'domain verification failure'
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const brandRefreshEnabled = isBrandRefreshEnabled()
  const SignInButton: React.ElementType = brandRefreshEnabled
    ? BrandRefreshButton
    : SignInLegacy
  const signInVariantProps = brandRefreshEnabled
    ? { variant: 'primary', style: { textTransform: 'uppercase' as const } }
    : { buttonType: 'primary' }
  const GoBackButton: React.ElementType = brandRefreshEnabled
    ? BrandRefreshButton
    : GoBackLegacy
  const goBackVariantProps = brandRefreshEnabled
    ? { variant: 'secondary', style: { textTransform: 'uppercase' as const } }
    : { buttonType: 'secondary' }
  const OfflineButton: React.ElementType = brandRefreshEnabled
    ? BrandRefreshButton
    : MakeAPaymentOfflineButtonForLWSLegacy
  const offlineVariantProps = brandRefreshEnabled
    ? { variant: 'secondary', style: { textTransform: 'uppercase' as const } }
    : { buttonType: 'secondary' }

  return (
    <LWSErrorContainer>
      <ErrorImage
        src={`${config.ASSETS_HOST}/assets/vista/PlugDisconnect.svg`}
        alt=""
      />
      <ErrorContent>
        <ErrorTitle>{i18n({ errors: 'cannotLoadAccounts' })}</ErrorTitle>
        <ErrorMessage>{i18n({ errors: 'pleaseTryAgain' })}</ErrorMessage>
      </ErrorContent>
      <ButtonContainer>
        <SignInButton
          {...signInVariantProps}
          {...LWS_ERROR_DATA_ATTRIBUTES}
          data-reason="sign into account manager"
          onClick={handleSignIn}
        >
          {i18n({ button: 'signInToAccountManager' })}
        </SignInButton>
        {state?.searchParams ? (
          <GoBackButton
            {...goBackVariantProps}
            {...LWS_ERROR_DATA_ATTRIBUTES}
            data-reason={`back to ${lwsBacktoMerchant[client]}`}
            onClick={handleBackToMerchant}
          >
            {i18n({ button: 'backTo' })} {lwsBacktoMerchant[client]}
          </GoBackButton>
        ) : (
          <OfflineButton
            {...offlineVariantProps}
            {...LWS_ERROR_DATA_ATTRIBUTES}
            data-reason="still looking to make a payment"
            data-cy="make-a-payment-offline-button"
            onClick={handleMakeAPaymentOffline}
          >
            {i18n({ button: 'makeAPaymentOffline' })}
          </OfflineButton>
        )}
      </ButtonContainer>
    </LWSErrorContainer>
  )
}

export default LWSError
